import React, { useEffect, useState } from "react";
import { Formik, Field, Form } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import _get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/lib/Shared/Button";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { useToast } from "hooks/useToast";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import { updateTransaction } from "state/slices/transaction";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { handleFilesUpload } from "utils/filesUtils";
import { Constants } from "config/constants";
import {
  directorApprovalsValidationSchema,
  shareholderApprovalsValidationSchema,
} from "./signatureDocValidationSchema";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

const SignatureDocument = ({
  selectedTab,
  document,
  documentType,
  signedDocumentType,
  actionType,
  fileUploads,
  setFormData,
  error,
}) => {
  const dispatch = useDispatch();
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions } = transactionUnderReview?.items;
  const { approvalDocs } = transactionUnderReview.items;
  const officialIndex = approvalDocs.findIndex(
    (doc) => Object.values(doc)[0]._id === document._id
  );
  const documentName = Object.keys(approvalDocs[officialIndex]);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();
  const { companyId, trxId } = useParams();
  const [transaction, setTransaction] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (transactions?.length > 0) {
      setTransaction(transactions.find((trx) => trx._id === trxId));
    }
  }, [transactions]);

  const handleDownloadDocuments = async () => {
    try {
      setIsDownloading(true);
      const businessDocuments = [];

      getChangedDocumentsModuleData(documentType).forEach(
        (businessDocument) => {
          const fileKey = fileUploads?.[businessDocument.name];
          if (fileKey) {
            businessDocuments.push({
              key: fileKey,
              name: `${businessDocument.label}.${fileKey.split(".").pop()}`,
            });
          }
        }
      );

      await Promise.all(
        businessDocuments.map(async (businessDocument) => {
          return dispatch(
            downloadBusinessDocumentsAsync({
              companyId,
              businessDocument,
            })
          );
        })
      );
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong, please try again"
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      /** Handle for director approval docs */
      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DIRECTOR_SIGNATURE:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_REVIEW;

      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_DR_CORRECTIONS:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
          .REGISTRAR_DR_REVIEW_II;

      /** Handle for shareholder approval docs */
      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SHAREHOLDER_SIGNATURE:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_REVIEW;

      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_SH_CORRECTIONS:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
          .REGISTRAR_SH_REVIEW_II;

      default:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    }
  };

  const handleSubmit = async (values) => {
    const uploadedFiles = await handleFilesUpload(values.fileUploads);
    let approvalDocs = {}
    if (selectedTab?.id === "director_approval") {
      approvalDocs = {
        ...transaction.approvalDocs,
        directorApprovalDocs: {
          ...transaction.approvalDocs.directorApprovalDocs,
          signedPdf: uploadedFiles.signedDirectorApprovalDocs
        }
      }
    }
    if (selectedTab?.id === "shareholder_approval") {
      approvalDocs = {
        ...transaction.approvalDocs,
        shareholderApprovalDocs: {
          ...transaction.approvalDocs.shareholderApprovalDocs,
          signedPdf: uploadedFiles.signedShareholderApprovalDocs
        }
      }
    }
    const response = await dispatch(
      updateTransaction({
        trxId,
        data: {
          approvalDocs,
          status: getNextStatus(transaction.status),
        },
      })
    );
    history.push(`/companies/${companyId}/dashboard`);
    toast("success", "Submitted to Registrar for review!");
  };

  let validationSchema;
  let signedFileUploads = {};
  if (selectedTab?.id === "director_approval") {
    validationSchema = directorApprovalsValidationSchema;
    signedFileUploads = {
      signedDirectorApprovalDocs: "",
    }
  } else if (selectedTab?.id === "shareholder_approval") {
    validationSchema = shareholderApprovalsValidationSchema;
    signedFileUploads = {
      signedShareholderApprovalDocs: "",
    }
  }

  if (!transaction) {
    return (
      <div>
        <SpinnerIcon />
      </div>
    );
  }
  return (
    <div className="bg-[#EDF0F5] py-4 px-4 mt-4 flex gap-5">
      <div className="w-full">
        <div className="px-2">Unsigned version</div>
        <div className="p-3 mt-2 bg-white">
          <Formik
            enableReinitialize
            initialValues={{
              fileUploads,
            }}
            onSubmit={() => null}
          >
            {() => (
              <div className="pl-4">
                <Field
                  name="fileUploads"
                  component={FileUploadForm}
                  documentData={getChangedDocumentsModuleData(documentType)}
                  resolvedFields={_get(document, "resolvedFields")}
                  onlyPreview
                  showDocumentLabel
                  editorKey={document.fileName}
                  documentName={documentType}
                />
              </div>
            )}
          </Formik>
        </div>
        <div className="mt-4">
          <Button
            className="ml-auto"
            onClick={handleDownloadDocuments}
            isLoading={isDownloading}
            loadingText="Downloading..."
          >
            Download unsigned version
          </Button>
        </div>
      </div>
      <div className="w-full">
        <div className="px-2">Signed version</div>
        <div className="p-3 mt-2 bg-white">
          <Formik
            enableReinitialize
            initialValues={{
              fileUploads: signedFileUploads,
            }}
            onSubmit={(values) => handleSubmit(values)}
            validationSchema={validationSchema}
          >
            {({ values, errors, isSubmitting }) => {
              return (
                <Form className="pl-4">
                  <Field
                    name="fileUploads"
                    component={FileUploadForm}
                    documentData={getChangedDocumentsModuleData(
                      signedDocumentType
                    )}
                    resolvedFields={_get(document, "resolvedFields")}
                    onlyPreview={false}
                    showDocumentLabel
                    editorKey={document.fileName}
                    documentName={signedDocumentType}
                    showRequiredAsterisk
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      loadingText="Submitting..."
                      isLoading={isSubmitting}
                      className="ml-auto mt-4"
                    >Submit to Registrar</Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        {error && <ErrorBanner errors={error} />}
      </div>
    </div>
  );
};

export default SignatureDocument;
